/**
 * Route guards.
 *
 * Both of them wait for the session to resolve before deciding anything. Treating "not known
 * yet" as "signed out" bounces every signed-in user to the login page on a hard refresh, and the
 * bug is invisible in development because the first `/auth/me` is usually already cached.
 *
 * Not every authenticated screen is behind these. The transcript viewer deliberately renders a
 * sign-in prompt in place rather than redirecting: the content is licensed, the restriction is
 * expected, and a redirect loses the page the person was trying to read.
 */
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, type Location } from 'react-router';

import { EmptyState, Spinner, Stack } from '@catalog/ui';

import { useAuth } from './auth-context.js';

const LOGIN_PATH = '/login';

export interface GuardProps {
  /** Rendered when the check passes. Defaults to the nested route's element. */
  children?: ReactNode;
}

/** Centred spinner, used while `/auth/me` is in flight. */
function Resolving() {
  return (
    <Stack align="center" gap="8" style={{ paddingBlock: 'var(--ex-space-12)' }}>
      <Spinner size="lg" label="Checking your session" />
    </Stack>
  );
}

/**
 * The redirect to sign-in, with the loop guard.
 *
 * A guard that redirects to `/login` *from* `/login` re-renders, redirects again with the new
 * location, and the `returnTo` nests one level deeper every time — the symptom is
 * "Maximum update depth exceeded" and a URL that grows without bound. The real route table does
 * not place a guard over `/login`, so this cannot happen there today; it costs one comparison to
 * make the component safe wherever it is mounted, and this is the kind of mistake whose symptom
 * does not name its cause.
 */
function signInRedirect(location: Location): ReactNode {
  if (location.pathname === LOGIN_PATH) return null;

  const returnTo = `${location.pathname}${location.search}`;

  return <Navigate to={`${LOGIN_PATH}?returnTo=${encodeURIComponent(returnTo)}`} replace />;
}

export function ProtectedRoute({ children }: GuardProps) {
  const { isAuthenticated, isPending } = useAuth();
  const location = useLocation();

  if (isPending) return <Resolving />;
  if (!isAuthenticated) return <>{signInRedirect(location)}</>;

  return <>{children ?? <Outlet />}</>;
}

/**
 * Admin only.
 *
 * An authenticated non-admin is refused in place rather than redirected to the login page:
 * sending them somewhere to sign in when they are already signed in is a loop, and the honest
 * answer is that this account does not have access.
 */
export function AdminRoute({ children }: GuardProps) {
  const { isAuthenticated, isAdmin, isPending } = useAuth();
  const location = useLocation();

  if (isPending) return <Resolving />;
  if (!isAuthenticated) return <>{signInRedirect(location)}</>;

  if (!isAdmin) {
    return (
      <EmptyState
        title="You do not have access to this area"
        description="The admin area is limited to administrator accounts. If you think you should have access, ask an administrator to update your role."
      />
    );
  }

  return <>{children ?? <Outlet />}</>;
}
